import { useEffect, useMemo, useState } from "react";

// react-table components
// @ts-ignore
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy, useExpanded } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Pagination from "@mui/material/Pagination";

// components
import FaiBox from "../../MuiComponents/FaiBox";
import FaiTypography from "../../MuiComponents/FaiTypography";
import FaiSelect from "../../MuiComponents/FaiSelect";
import FaiInput from "../../MuiComponents/FaiInput";
import FaiPagination from "../../MuiComponents/FaiPagination";

// components
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

function DataTable({
    entriesPerPage,
    canSearch,
    showTotalEntries,
    table,
    pagination,
    isSorted,
    noEndBorder,
    sizeSelector,
    showHeader,
    expandRows,
    expandedRowObj,
    renderRowSubComponent,
    onPageChange, // <-- new prop for controlled pagination
}) {
    const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
    const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
    const columns = useMemo(() => table.columns, [table]);
    const data = useMemo(() => table.rows, [table]);

    // If external pagination is provided, use controlled mode
    const isControlledPagination = pagination && typeof pagination.totalPages === 'number';
    const [internalPage, setInternalPage] = useState(0);
    const [internalPageSize, setInternalPageSize] = useState(defaultValue);

    // For controlled pagination, use external values; otherwise, use internal state
    const pageIndex = isControlledPagination ? (pagination.page ?? 0) : internalPage;
    const pageSize = isControlledPagination ? (pagination.pageSize ?? defaultValue) : internalPageSize;
    const totalPages = isControlledPagination ? pagination.totalPages : undefined;
    const hasNext = isControlledPagination ? pagination.hasNext : undefined;
    const hasPrevious = isControlledPagination ? pagination.hasPrevious : undefined;
    const numberOfElements = isControlledPagination ? pagination.numberOfElements : data.length;
    const totalElements = isControlledPagination ? pagination.totalElements : data.length;

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: 0,
                expanded: expandRows && expandedRowObj.hasOwnProperty(0) ? expandedRowObj : {}
            },
            manualPagination: isControlledPagination,
            pageCount: isControlledPagination ? totalPages : undefined,
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,
        pageOptions,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setGlobalFilter,
        state: { globalFilter },
    } = tableInstance;

    // Set the default value for the entries per page when component mounts
    useEffect(() => {
        if (!isControlledPagination) setPageSize(defaultValue || 10);
    }, [defaultValue, isControlledPagination, setPageSize]);

    // Set the entries per page value based on the select value
    const setEntriesPerPage = ({ value }) => {
        if (isControlledPagination) {
            if (onPageChange) onPageChange(0, value); // reset to first page
        } else {
            setPageSize(value);
            setInternalPage(0);
        }
    };

    // Render the paginations
    const renderPagination = () => {
        if (isControlledPagination) {
            const items = [];
            for (let i = 0; i < totalPages; i++) {
                items.push(
                    <FaiPagination
                        item
                        key={i}
                        onClick={() => onPageChange && onPageChange(i, pageSize)}
                        active={pageIndex === i}
                    >
                        {i + 1}
                    </FaiPagination>
                );
            }
            return items;
        } else {
            return pageOptions.map((option) => (
                <FaiPagination
                    item
                    key={option}
                    onClick={() => gotoPage(Number(option))}
                    active={pageIndex === option}
                >
                    {option + 1}
                </FaiPagination>
            ));
        }
    };

    // Handler for the input to set the pagination index
    const handleInputPagination = ({ target: { value } }) => {
        const pageNum = Number(value) - 1;
        if (isControlledPagination) {
            if (onPageChange) onPageChange(pageNum, pageSize);
        } else {
            if (pageNum >= 0 && pageNum < pageOptions.length) gotoPage(pageNum);
        }
    };

    // Customized page options starting from 1
    const customizedPageOptions = isControlledPagination
        ? Array.from({ length: totalPages }, (_, i) => i + 1)
        : pageOptions.map((option) => option + 1);

    // Search input value state
    const [search, setSearch] = useState(globalFilter);

    // Search input state handle
    const onSearchChange = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 100);

    // A function that sets the sorted value for the table
    const setSortedValue = (column) => {
        let sortedValue;

        if (isSorted && column.isSorted) {
            sortedValue = column.isSortedDesc ? "desc" : "asce";
        } else if (isSorted) {
            sortedValue = "none";
        } else {
            sortedValue = false;
        }

        return sortedValue;
    };

    // Setting the entries starting and ending point
    let entriesStart, entriesEnd;
    if (isControlledPagination) {
        entriesStart = totalElements === 0 ? 0 : pageIndex * pageSize + 1;
        entriesEnd = totalElements === 0 ? 0 : entriesStart + numberOfElements - 1;
    } else {
        entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;
        if (pageIndex === 0) {
            entriesEnd = pageSize;
        } else if (pageIndex === pageOptions.length - 1) {
            entriesEnd = rows.length;
        } else {
            entriesEnd = pageSize * (pageIndex + 1);
        }
    }

    return (
        <TableContainer sx={{ boxShadow: "none" }}>
            {/* {(sizeSelector && entriesPerPage) || canSearch ? (
                <FaiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                    {sizeSelector && entriesPerPage && (
                        <FaiBox display="flex" alignItems="center">
                            <FaiBox width="25%">
                                <FaiSelect
                                    defaultValue={{ value: defaultValue, label: defaultValue }}
                                    options={entries.map((entry) => ({ value: entry, label: entry }))}
                                    onChange={setEntriesPerPage}
                                    size="small"
                                />
                            </FaiBox>
                            <FaiTypography variant="caption" color="secondary">
                                &nbsp;&nbsp;entries per page
                            </FaiTypography>
                        </FaiBox>
                    )}
                    {canSearch && (
                        <FaiBox width="12rem" ml="auto">
                            <FaiInput
                                placeholder="Search..."
                                value={search}
                                onChange={({ currentTarget }) => {
                                    setSearch(search);
                                    onSearchChange(currentTarget.value);
                                }}
                            />
                        </FaiBox>
                    )}
                </FaiBox>
            ) : null} */}
            <Box sx={{ overflowX: "auto" }}>
                <Table {...getTableProps()}>
                    <FaiBox component="thead">
                        {showHeader && headerGroups.map((headerGroup, key) => (
                            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column, index) => (
                                    <DataTableHeadCell
                                        key={index}
                                        {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                                        width={column.width ? column.width : "auto"}
                                        align={column.align ? column.align : "left"}
                                        sorted={setSortedValue(column)}
                                    >
                                        {column.render("Header")}
                                    </DataTableHeadCell>
                                ))}
                            </TableRow>
                        ))}
                    </FaiBox>
                    <TableBody {...getTableBodyProps()}>
                        {page.map((row, key) => {
                            prepareRow(row);
                            return (
                                <>
                                    <TableRow key={key} {...row.getRowProps()}>
                                        {row.cells.map((cell, index) => (
                                            <DataTableBodyCell
                                                key={index}
                                                noBorder={noEndBorder && rows.length - 1 === key}
                                                align={cell.column.align ? cell.column.align : "left"}
                                                {...cell.getCellProps()}
                                            >
                                                {cell.render("Cell")}
                                            </DataTableBodyCell>
                                        ))}
                                    </TableRow>
                                    {row.isExpanded ? (
                                        <tr>
                                            <td colSpan={row.cells.length}>
                                                <span className="subTable">
                                                    {renderRowSubComponent(row)}
                                                </span>
                                            </td>
                                        </tr>
                                    ) : null}
                                </>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>

            <FaiBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={!showTotalEntries && (isControlledPagination ? totalPages === 1 : pageOptions.length === 1) ? 0 : 3}
                bgColor={"#F1EBE7"}
            >
                {showTotalEntries && (
                    <FaiBox mb={{ xs: 3, sm: 0 }}>
                        <FaiTypography variant="button" color="secondary" fontWeight="regular">
                            Showing {entriesStart} to {entriesEnd} of {totalElements} entries
                        </FaiTypography>
                    </FaiBox>
                )}
                {(isControlledPagination ? totalPages > 1 : pageOptions.length > 1) && (
                    <Pagination
                        count={isControlledPagination ? totalPages : pageOptions.length}
                        page={isControlledPagination ? pageIndex + 1 : pageIndex + 1}
                        onChange={(event, value) => {
                            // value is 1-based, so subtract 1 for 0-based pageIndex
                            if (isControlledPagination) {
                                if (onPageChange) onPageChange(value - 1, pageSize);
                            } else {
                                gotoPage(value - 1);
                            }
                        }}
                        color={pagination && pagination.color ? pagination.color : "primary"}
                        shape="rounded"
                        showFirstButton
                        showLastButton
                    />
                )}
            </FaiBox>
        </TableContainer>
    );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
    entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
    canSearch: false,
    showTotalEntries: true,
    pagination: { variant: "gradient", color: "info" },
    isSorted: false,
    noEndBorder: false,
    sizeSelector: false,
    showHeader: true,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
    entriesPerPage: PropTypes.oneOfType([
        PropTypes.shape({
            defaultValue: PropTypes.number,
            entries: PropTypes.arrayOf(PropTypes.number),
        }),
        PropTypes.bool,
    ]),
    showHeader: PropTypes.bool,
    canSearch: PropTypes.bool,
    showTotalEntries: PropTypes.bool,
    table: PropTypes.objectOf(PropTypes.array).isRequired,
    pagination: PropTypes.shape({
        variant: PropTypes.oneOf(["contained", "gradient"]),
        color: PropTypes.oneOf([
            "primary",
            "secondary",
            "info",
            "success",
            "warning",
            "error",
            "dark",
            "light",
        ]),
        first: PropTypes.bool,
        hasNext: PropTypes.bool,
        hasPrevious: PropTypes.bool,
        last: PropTypes.bool,
        numberOfElements: PropTypes.number,
        totalElements: PropTypes.number,
        totalPages: PropTypes.number,
        page: PropTypes.number,
        pageSize: PropTypes.number,
    }),
    isSorted: PropTypes.bool,
    noEndBorder: PropTypes.bool,
    sizeSelector: PropTypes.bool,
    expandRows: PropTypes.bool,
    expandedRowObj: PropTypes.objectOf(PropTypes.array),
    renderRowSubComponent: PropTypes.any,
    onPageChange: PropTypes.func,
};

export default DataTable;
